/**
 * Canonical marketing-channel classifier (plan P0.2). Pure, static-table-only
 * module — no D1/env/next imports. Ships to the CLIENT bundle: keep under
 * 4 KB raw (`pnpm bundle:attribution`). First match wins; rule order is
 * load-bearing — see `tests/unit/analytics/channel.test.ts`.
 */

import { classifyAiReferrer } from "@/lib/analytics/ai-referrer";
import { APEX_DOMAIN } from "@/lib/cookies/apex-domain";

export const MARKETING_CHANNELS = [
	"paid_search",
	"paid_social",
	"display",
	"affiliate",
	"organic_search",
	"ai_search",
	"linkedin",
	"x",
	"reddit",
	"facebook",
	"instagram",
	"tiktok",
	"youtube",
	"community",
	"newsletter",
	"cold_email",
	"referral",
	"partnership",
	"job_share",
	"direct_sales",
	"direct",
	"unknown",
] as const;

export type MarketingChannel = (typeof MARKETING_CHANNELS)[number];

/** Mirrors `acquisitionTouches.clickIdKind` in `src/lib/db/schema.ts`. */
export type ClickIdKind =
	| "gclid"
	| "gbraid"
	| "wbraid"
	| "fbclid"
	| "msclkid"
	| "ttclid"
	| "li_fat_id"
	| "twclid";

export type ChannelInput = {
	utmSource?: string | null;
	utmMedium?: string | null;
	utmCampaign?: string | null;
	clickIdKind?: string | null;
	/** Pre self-referral-suppressed — see {@link isSelfReferral}. */
	referrerHost?: string | null;
	hasReferralCode?: boolean;
};

const PAID_SEARCH_CLICK_IDS = new Set(["gclid", "gbraid", "wbraid", "msclkid"]);
const PAID_SEARCH_MEDIUMS = new Set([
	"cpc",
	"ppc",
	"paidsearch",
	"paid-search",
	"sem",
]);
const PAID_SOCIAL_CLICK_IDS = new Set([
	"fbclid",
	"ttclid",
	"li_fat_id",
	"twclid",
]);
const PAID_SOCIAL_MEDIUMS = new Set([
	"paid",
	"cpc",
	"paid_social",
	"paidsocial",
]);
const DISPLAY_MEDIUMS = new Set(["display", "banner", "cpm"]);
// Inbound affiliate/CPA self-tags — distinct from the OUTBOUND registry in
// src/lib/affiliate/*. No inbound deal is live; extend only when one ships.
const AFFILIATE_SOURCES = new Set([
	"accesstrade",
	"involve_asia",
	"impact",
	"cj_affiliate",
	"partnerstack",
]);
const COLD_EMAIL_SOURCES = new Set(["outreach", "coldmail", "b2b"]);
const NEWSLETTER_MEDIUMS = new Set(["email", "newsletter"]);

// utm_medium (SocialPlatform, src/lib/social/utm.ts) -> channel for sp_/lt_.
// threads/pinterest omitted (not in MARKETING_CHANNELS) -> fall to unknown.
const SOCIAL_MEDIUM_MAP: Readonly<Record<string, MarketingChannel>> = {
	facebook: "facebook",
	instagram: "instagram",
	tiktok: "tiktok",
	youtube: "youtube",
	linkedin: "linkedin",
	twitter: "x",
	x: "x",
	reddit: "reddit",
};

const SEARCH_HOSTS: readonly string[] = [
	"google.com",
	"google.co.id",
	"google.co.uk",
	"google.de",
	"bing.com",
	"duckduckgo.com",
	"yandex.com",
	"yandex.ru",
	"baidu.com",
	"ecosia.org",
	"brave.com",
	"search.brave.com",
	"yahoo.com",
	"yahoo.co.jp",
	"search.yahoo.com",
	"googlequicksearchbox", // Android Google-app in-app search referrer.
];

// t.co is Twitter's shortener -> x, NOT community (see COMMUNITY_HOSTS).
const SOCIAL_HOSTS: ReadonlyArray<readonly [string, MarketingChannel]> = [
	["linkedin.com", "linkedin"],
	["lnkd.in", "linkedin"],
	["x.com", "x"],
	["twitter.com", "x"],
	["t.co", "x"],
	["reddit.com", "reddit"],
	["redd.it", "reddit"],
	["facebook.com", "facebook"],
	["fb.com", "facebook"],
	["instagram.com", "instagram"],
	["tiktok.com", "tiktok"],
	["youtube.com", "youtube"],
	["youtu.be", "youtube"],
];

// t.co deliberately excluded — it's Twitter's shortener (SOCIAL_HOSTS above).
const COMMUNITY_HOSTS: readonly string[] = [
	"news.ycombinator.com",
	"indiehackers.com",
	"dev.to",
	"lobste.rs",
	"kaskus.co.id",
	"discord.com",
];

// Seeded empty: no inbound co-marketing referrer domain confirmed live yet.
// utm_source === 'partnership' (rule 14) stays reachable regardless.
const PARTNER_HOSTS: readonly string[] = [];

function normalizeToken(v: string | null | undefined): string | null {
	if (!v) return null;
	const t = v.trim().toLowerCase();
	return t.length > 0 ? t : null;
}

function normalizeHost(v: string | null | undefined): string | null {
	const h = normalizeToken(v);
	if (!h) return null;
	return h.startsWith("www.") ? h.slice(4) : h;
}

function hostMatches(host: string, domain: string): boolean {
	return host === domain || host.endsWith(`.${domain}`);
}

function matchesHostList(host: string, list: readonly string[]): boolean {
	return list.some((d) => hostMatches(host, d));
}

function matchSocialHost(host: string): MarketingChannel | null {
	for (const [domain, channel] of SOCIAL_HOSTS) {
		if (hostMatches(host, domain)) return channel;
	}
	return null;
}

/**
 * True when `host` is lokerdollar's own domain (apex or subdomain). Callers
 * MUST drop the referrer before calling {@link classifyChannel} — plan
 * P0.3(a): 16 lifetime users record `app.lokerdollar.com` as their own source.
 */
export function isSelfReferral(host: string | null | undefined): boolean {
	const h = normalizeHost(host);
	return !!h && (h === APEX_DOMAIN || h.endsWith(`.${APEX_DOMAIN}`));
}

/** Never throws; untagged is `direct`, unclassifiable is `unknown`. */
export function classifyChannel(input: ChannelInput): MarketingChannel {
	const utmSource = normalizeToken(input.utmSource);
	const utmMedium = normalizeToken(input.utmMedium);
	const utmCampaign = normalizeToken(input.utmCampaign);
	const clickIdKind = normalizeToken(input.clickIdKind);
	const referrerHost = normalizeHost(input.referrerHost);

	if (clickIdKind && PAID_SEARCH_CLICK_IDS.has(clickIdKind))
		return "paid_search"; // 1
	if (utmMedium && PAID_SEARCH_MEDIUMS.has(utmMedium)) return "paid_search"; // 2
	if (
		clickIdKind &&
		PAID_SOCIAL_CLICK_IDS.has(clickIdKind) &&
		utmMedium &&
		PAID_SOCIAL_MEDIUMS.has(utmMedium)
	)
		return "paid_social"; // 3
	if (utmMedium && DISPLAY_MEDIUMS.has(utmMedium)) return "display"; // 4
	if (
		utmMedium === "affiliate" ||
		(utmSource && AFFILIATE_SOURCES.has(utmSource))
	)
		return "affiliate"; // 5
	if (utmMedium === "email" && utmSource && COLD_EMAIL_SOURCES.has(utmSource))
		return "cold_email"; // 6
	if (utmMedium && NEWSLETTER_MEDIUMS.has(utmMedium)) return "newsletter"; // 7
	if (
		utmCampaign &&
		(utmCampaign.startsWith("sp_") || utmCampaign.startsWith("lt_"))
	)
		return (utmMedium && SOCIAL_MEDIUM_MAP[utmMedium]) || "unknown"; // 8
	if (input.hasReferralCode || utmSource === "referral") return "referral"; // 9
	if (classifyAiReferrer(referrerHost) !== null) return "ai_search"; // 10, before 12
	if (referrerHost && matchesHostList(referrerHost, SEARCH_HOSTS))
		return "organic_search"; // 11
	if (referrerHost) {
		const social = matchSocialHost(referrerHost); // 12
		if (social) return social;
	}
	if (referrerHost && matchesHostList(referrerHost, COMMUNITY_HOSTS))
		return "community"; // 13
	if (
		utmSource === "partnership" ||
		(referrerHost && matchesHostList(referrerHost, PARTNER_HOSTS))
	)
		return "partnership"; // 14
	if (utmSource === "job_share") return "job_share"; // 15
	if (!referrerHost && !utmSource && !utmMedium && !utmCampaign && !clickIdKind)
		return "direct"; // 16
	return "unknown"; // 17
}
